/**
 * Supabase Client Configuration
 *
 * Uses the new Supabase API keys (2025+):
 * - VITE_SUPABASE_PUBLISHABLE_KEY (sb_publishable_...) - for client-side use
 * - SUPABASE_SECRET_KEY (sb_secret_...) - for server-side/Edge Functions only
 *
 * The old JWT-based anon/service_role keys are deprecated.
 * See: https://github.com/orgs/supabase/discussions/29260
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { shouldDetectSessionInUrl } from './authCallbackDetection';
import { env } from '@/lib/env';
import { withJourneyPlayerCapability } from './journeyPlayerCapability';
import { withServerActivityTiming } from '@/utils/journeyServerClock';
import { withPasswordUpdateTimeout } from './passwordUpdateTimeout';

// Required-variable validation lives in src/lib/env.ts, which throws at module
// load if either of these is missing. That validation is skipped under
// MODE === 'test', so keep this explicit guard too: it restores the clear,
// actionable error for a test/CI run without a .env (createClient would
// otherwise throw a cryptic internal error). In prod the vars are always set,
// so this never fires.
const SUPABASE_URL = env.supabaseUrl;
const SUPABASE_PUBLISHABLE_KEY = env.supabasePublishableKey;
if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    '[supabase] Missing required environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in .env'
  );
}
const isBrowser = typeof window !== 'undefined';
const authStorage = isBrowser ? window.localStorage : undefined;
const detectSessionInUrl = shouldDetectSessionInUrl({
  isBrowser,
  pathname: isBrowser ? window.location.pathname : undefined,
});

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: authStorage,
    persistSession: isBrowser,
    autoRefreshToken: isBrowser,
    detectSessionInUrl,
    flowType: 'pkce',
  },
  global: {
    // REST-only capability header; see journeyPlayerCapability.ts. The
    // server-activity wrapper only observes timing for journey_timing.
    fetch: withJourneyPlayerCapability(withServerActivityTiming(withPasswordUpdateTimeout((input, init) => fetch(input, init)))),
  },
});
